import { useRef } from 'react'
import { Canvas, useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import { Ember } from './Ember'
import type { CharacterShape } from '@/game/characters'

interface EmberPreviewProps {
  color: string
  trailColor?: string | null
  reducedMotion?: boolean
  shape?: CharacterShape
  equippedProps?: string[]
  characterId?: string
}

function OrbitTrail({ color }: { color: string }) {
  const group = useRef<THREE.Group>(null)
  const count = 18
  useFrame((state) => {
    const g = group.current
    if (!g) return
    const t = state.clock.elapsedTime
    g.children.forEach((child, i) => {
      const a = t * 1.2 - i * 0.32
      const r = 0.72
      child.position.set(Math.cos(a) * r, 0.95 + Math.sin(t * 2 + i) * 0.05, Math.sin(a) * r)
      const s = (1 - i / count) * 0.12 + 0.02
      child.scale.setScalar(s)
      const mat = (child as THREE.Mesh).material as THREE.MeshBasicMaterial
      mat.opacity = (1 - i / count) * 0.85
    })
  })
  return (
    <group ref={group}>
      {Array.from({ length: count }, (_, i) => (
        <mesh key={i}>
          <icosahedronGeometry args={[1, 0]} />
          <meshBasicMaterial color={color} transparent toneMapped={false} />
        </mesh>
      ))}
    </group>
  )
}

function SpinRig({ children, reducedMotion }: { children: React.ReactNode; reducedMotion: boolean }) {
  const ref = useRef<THREE.Group>(null)
  useFrame((state) => {
    if (!ref.current) return
    ref.current.rotation.y = reducedMotion ? 0 : Math.sin(state.clock.elapsedTime * 0.5) * 0.55
  })
  return <group ref={ref}>{children}</group>
}

export function EmberPreview({ color, trailColor, reducedMotion = false, shape, equippedProps, characterId = 'default_hero' }: EmberPreviewProps) {
  return (
    <Canvas
      dpr={[1, 2]}
      camera={{ position: [0, 0.15, 3.1], fov: 42 }}
      gl={{ antialias: true, alpha: true }}
    >
      <ambientLight intensity={0.5} />
      <directionalLight position={[3, 4, 3]} intensity={1.1} color="#fff1dc" />
      <pointLight position={[-3, 1, -2]} intensity={0.6} color={color} />
      {trailColor && trailColor !== 'none' && <OrbitTrail color={trailColor} />}
      <group position={[0, -0.95, 0]}>
        <SpinRig reducedMotion={reducedMotion}>
          <Ember
            color={color}
            phase="idle"
            slideDir={null}
            facing="down"
            reducedMotion={reducedMotion}
            shape={shape}
            characterId={characterId}
            billboard={false}
            equippedProps={equippedProps}
          />
        </SpinRig>
      </group>
    </Canvas>
  )
}
